import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxIconComponent,
  onyxLengthValidator,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
  OnyxToggleComponent,
  OnyxUploadComponent,
} from '@onyx/angular';
import { map } from 'rxjs';
import type { Country } from '../../../../common/enums/country';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';

type DriverCardFormGroup = ReturnType<typeof DriverCardFormComponent.buildForm>;

@Component({
  selector: 'app-driver-card-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    OnyxDatepickerComponent,
    OnyxUploadComponent,
    AsyncPipe,
    OnyxToggleComponent,
    OnyxIconComponent,
  ],
  templateUrl: './driver-card-form.component.html',
  styleUrl: './driver-card-form.component.scss',
})
export class DriverCardFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<DriverCardFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly nationality = input.required<Country | null>();
  public readonly driversLicenseCategories = input.required<string[]>();

  protected readonly I18N = 'drivers.driverForm.driverCard';

  protected readonly categoriesRequireDriverCard = toSignal(
    this.dictionariesService
      .getDictionary(DictionaryCode.DRIVERS_LICENSE_CATEGORY)
      .pipe(
        map((categories) =>
          categories.filter((category) => category.requireDriverCard),
        ),
      ),
  );
  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  protected categoriesRequireDriverCardLabel = computed(() =>
    this.categoriesRequireDriverCard()
      ?.map((category) => category.name)
      .join(', '),
  );
  protected isRequired = computed(() =>
    this.driversLicenseCategories().some((category) =>
      this.categoriesRequireDriverCard()?.find((c) => c.value === category),
    ),
  );
  protected showForm = signal(true);

  public ngOnInit() {
    this.showForm.set(!!this.form().controls.issuingCountry.value);

    effect(
      () => {
        const showForm = this.showForm() || this.isRequired();
        ValidationHelper.toggleControls(this.form(), {
          enabled: showForm,
          reset: true,
        });

        untracked(() => {
          const issuingCountryControl = this.form().controls.issuingCountry;

          if (
            showForm &&
            (this.mode() !== OnyxFormMode.EDIT || !issuingCountryControl.value)
          ) {
            issuingCountryControl.setValue(this.nationality());
          }
        });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      number: fb.control<string | null>(null, [
        Validators.required,
        onyxLengthValidator(16),
      ]),
      issuingCountry: fb.control<Country | null>(null, [Validators.required]),
      expirationDate: fb.control<string | null>(null, [Validators.required]),
      scans: fb.control<string[]>([]),
    });
  }
}
